import api from './api';

export interface Project {
  id: string;
  title?: string;
  description?: string;
  location?: string;
  valuation_usd?: number;
  tokenized_percent?: number;
  project_type: 'real_estate' | 'invoice' | 'art' | 'equipment' | 'agriculture';
  user_id: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'tokenized';
  smart_contract_address?: string;
  token_name?: string;
  token_symbol?: string;
  created_at?: string;
  risk_score?: RiskScoreResponse;
}

export interface RiskScoreResponse {
  score: number;
  liquidity_score: number;
  credit_score: number;
  volatility_score: number;
  model_version?: string;
  generated_at?: string;
}

export interface SubmitProjectData {
  title: string;
  description: string;
  valuation_usd: number;
  tokenized_percent: number;
  project_type: 'real_estate' | 'invoice' | 'art' | 'equipment' | 'agriculture';
  user_id: string;
  location?: string;
  token_name?: string;
  token_symbol?: string;
}

export interface ProjectUpdateData {
  title?: string;
  description?: string;
  location?: string;
  valuation_usd?: number;
  tokenized_percent?: number;
  project_type?: 'real_estate' | 'invoice' | 'art' | 'equipment' | 'agriculture';
  token_name?: string;
  token_symbol?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'tokenized';
}

export interface ProjectListResponse {
  projects: Project[];
  total_count: number;
}

export interface ProjectFilters {
  status?: string;
  project_type?: string;
  min_valuation?: number;
  max_valuation?: number;
}

export interface PlatformStats {
  totalTokenizedVolume: number;
  totalProjects: number;
  tokenizedProjects: number;
  activeInvestors: number;
}

export const projectService = {
  // Get all projects
  async getProjects(): Promise<ProjectListResponse> {
    const response = await api.get('/projects');
    return response.data;
  },

  // Get project by ID
  async getProject(id: string): Promise<Project> {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  // Submit new project
  async submitProject(data: SubmitProjectData): Promise<any> {
    const response = await api.post('/submit-asset', data);
    return response.data;
  },

  // Update project
  async updateProject(id: string, data: ProjectUpdateData): Promise<any> {
    const response = await api.put(`/projects/${id}`, data);
    return response.data;
  },

  // Approve project (admin only)
  async approveProject(id: string, autoTokenize: boolean = false, tokenStandard: string = 'standard'): Promise<any> {
    const response = await api.post(`/projects/${id}/approve`, null, {
      params: {
        auto_tokenize: autoTokenize,
        token_standard: tokenStandard
      }
    });
    return response.data;
  },

  // Tokenize project
  async tokenizeProject(id: string): Promise<any> {
    const response = await api.post(`/projects/${id}/tokenize`);
    return response.data;
  },

  // Tokenize project with ERC-3643 standard
  async tokenizeProjectERC3643(id: string): Promise<any> {
    const response = await api.post(`/projects/${id}/tokenize-erc3643`);
    return response.data;
  },

  // Calculate risk score for a project
  async calculateRiskScore(projectId: string, valuationUsd: number, projectType: string): Promise<RiskScoreResponse> {
    const response = await api.post('/risk/score', {
      project_id: projectId,
      valuation_usd: valuationUsd,
      project_type: projectType
    });
    return response.data;
  },

  // Calculate platform stats from projects data
  calculatePlatformStats(projects: Project[]): PlatformStats {
    const tokenizedProjects = projects.filter(p => p.status === 'tokenized');
    const totalTokenizedVolume = tokenizedProjects.reduce((sum, p) => {
      const valuationPercent = (p.valuation_usd || 0) * ((p.tokenized_percent || 0) / 100);
      return sum + valuationPercent;
    }, 0);

    // Get unique user IDs from all projects
    const uniqueUsers = new Set(projects.map(p => p.user_id));

    return {
      totalTokenizedVolume,
      totalProjects: projects.length,
      tokenizedProjects: tokenizedProjects.length,
      activeInvestors: uniqueUsers.size,
    };
  },
}; 